import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { injectIntl, defineMessages } from 'react-intl';
import { decorateIntlMessages } from '../../common/rosetta_formatted_message';
import {
  EDITORIAL_CONTENT_NODE_ID,
  SOLD_CONTENT_TYPES,
  EDITION_ID_EN_US
} from '../../../constants';
import EpisodeMeta from './episode_meta';
import BrandingBanner from '../../common/branding_banner';
import SocialIcons from '../../common/social_icons';
import { SVGs } from '../../common/fa_icons';
import MediaQuery from '../../common/media_query';
import MoreInfoButton from './more_info_button';
import Comments from '../../article/comments';
import Sections from '../../section/sections';
import { sendAnalyticsEvent } from '../../../utils/analytics';
import { open, smoothScrollTo } from '../../../utils/dom';
import classNames from 'classnames';
import {get} from 'lodash';

const FORMATTED_MESSAGES = decorateIntlMessages(__filename, defineMessages({
  comment_button_title: {
    id: 'comment_button_title',
    description: 'Comment button title attribute content (Comment)',
    defaultMessage: 'Comment'
  },
  youtube_button_title: {
    id: 'youtube_button_title',
    description: 'Youtube button title attribute content (Open youtube channel)',
    defaultMessage: 'Open YouTube channel'
  }
}));

const SCROLL_DELAY_MS = 1000;

export class EpisodeBody extends Component {
  state = {
    showInfo: false,
    shownOnce: false
  };

  showInfo = () => this.setState({
    showInfo: true,
    shownOnce: true
  });

  hideOrShowInfo = () => this.setState({
    showInfo: !this.state.showInfo,
    shownOnce: true
  });

  handleHeaderClick = () => {
    this.hideOrShowInfo();
    sendAnalyticsEvent(
      'More Information',
      'Click',
      'Title & Subtitle'
    );
  };

  handleMoreInfoButtonClick = (event) => {
    event.stopPropagation();
    this.hideOrShowInfo();
    sendAnalyticsEvent(
      'More Information',
      'Click',
      'Icon'
    );
  };

  handleCommentsButtonClick = () => {
    if (this.state.showInfo) {
      this.scrollToComments();
    } else {
      this.showInfo();
      global.setTimeout(
        this.scrollToComments,
        SCROLL_DELAY_MS
      );
    }
    sendAnalyticsEvent(
      'Comments',
      'Click',
      this.props.article.url_full
    );
  };

  scrollToComments = () =>
    smoothScrollTo(this._commentsNode);

  youtubeChannel = () =>
    get(this.context.edition_data, 'config.youtube_channel', 'refinery29tv');

  youtubeURL = () =>
    'https://www.youtube.com/' + this.youtubeChannel();

  handleYouTubeButtonClick = (event) => {
    event.preventDefault();
    open(event.currentTarget.href, '_blank');
    sendAnalyticsEvent(
      'Youtube',
      'Click',
      this.props.article.url_full
    );
  };

  render() {
    const {
      shownOnce,
      showInfo: active
    } = this.state;
    const {
      episode,
      intl,
      meta_title,
      meta_description,
      show,
      spotID,
      textSections,
      article: {
        asset_main,
        campaign_branding_image_mobile_url,
        comments_id,
        opener_images,
        primary_category,
        program_branding_image_url,
        sanitizedTitle,
        url_full,
        excerpt_truncated_325,
        meta: {
          campaign_branding_clicktracker,
          campaign_branding_impressiontracker,
          campaign_branding_nofollow,
          program_branding_impression_tracker,
          program_branding_click_tracker,
          program_branding_nofollow,
          social_title,
          sold_content_type
        }
      }
    } = this.props;
    const { edition_data } = this.context;

    const socialIconProps = {
      asset_main,
      opener_images,
      sanitizedTitle,
      url_full,
      meta_title,
      meta_description,
      social_title,
      excerpt_truncated_325
    };

    const defaultSocialIcons = [
      'facebook',
      'twitter'
    ];
    const socialIcons = edition_data.id !== EDITION_ID_EN_US
      ? defaultSocialIcons
      : [...defaultSocialIcons, 'flipboard'];

    return (
      <div className="body">
        <div className={classNames('clickable-region', { active })}
          onClick={this.handleHeaderClick}
        >
          <MediaQuery breakpoint="notDesktop">
            <div className="episode-headers">
              <div className="show-title"
                dangerouslySetInnerHTML={{ __html: show.display_name }}
              />
              <span className="episode-title"
                dangerouslySetInnerHTML={{ __html: episode.title }}
              />
              <p className="description"
                dangerouslySetInnerHTML={{ __html: meta_description }}
              />
            </div>
          </MediaQuery>
          <MoreInfoButton active={this.state.showInfo}
            handleClick={this.handleMoreInfoButtonClick}
          />
        </div>
        <span className="social-icons">
          <div className="story-share">
            <button className="fa fa-comment"
              onClick={this.handleCommentsButtonClick}
              dangerouslySetInnerHTML={{ __html: SVGs.comment }}
              title={intl.formatMessage(FORMATTED_MESSAGES.comment_button_title)}
            />
          </div>
          <SocialIcons {...socialIconProps}
            className="video-series-top"
            icons={socialIcons}
            enableAdTextForShares={
              sold_content_type === SOLD_CONTENT_TYPES.CUSTOM
            }
          />
          <div className="story-share">
            <a href={this.youtubeURL()}
              onClick={this.handleYouTubeButtonClick}
              title={intl.formatMessage(FORMATTED_MESSAGES.youtube_button_title)}
            >
              <button className="fa fa-youtube-play"
                dangerouslySetInnerHTML={{ __html: SVGs['youtube-play'] }}
                title={intl.formatMessage(FORMATTED_MESSAGES.youtube_button_title)}
              />
            </a>
          </div>
        </span>
        <div className={classNames('info-container', {
          'show-info': this.state.showInfo,
          'no-content': !this.state.shownOnce
        })}
        >
          <div className="banners">
            {
              campaign_branding_image_mobile_url &&
              shownOnce &&
                <BrandingBanner type="campaign"
                  src={campaign_branding_image_mobile_url}
                  impressionTracker={campaign_branding_impressiontracker}
                  clickTracker={campaign_branding_clicktracker}
                  noFollow={campaign_branding_nofollow}
                />
            }
            {
              program_branding_image_url &&
              shownOnce &&
                <BrandingBanner type="program"
                  src={program_branding_image_url}
                  impressionTracker={program_branding_impression_tracker}
                  clickTracker={program_branding_click_tracker}
                  noFollow={program_branding_nofollow}
                />
            }
          </div>
          <div id={EDITORIAL_CONTENT_NODE_ID}>
            <Sections sections={textSections}
              shouldSendGAViewEvents={this.state.showInfo}
              shouldSendGAScrollDepthEvents={this.state.showInfo}
            />
          </div>
          <EpisodeMeta article={this.props.article}
            metaTitle={meta_title}
          />
          { spotID &&
            <div className="comments-container"
              ref={node => this._commentsNode = get(node, 'firstChild')}
            >
              <Comments darkTheme
                spotID={spotID}
                comments_id={comments_id}
                url_full={url_full}
                primary_category={primary_category}
              />
            </div>}
        </div>
      </div>
    );
  }
}

EpisodeBody.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired,
  article: PropTypes.shape({
    comments_id: PropTypes.string.isRequired,
    asset_main: PropTypes.object.isRequired,
    opener_images: PropTypes.arrayOf(PropTypes.object.isRequired),
    sanitizedTitle: PropTypes.string.isRequired,
    excerpt_truncated_325: PropTypes.string.isRequired,
    meta: PropTypes.shape({
      campaign_branding_clicktracker: PropTypes.string,
      campaign_branding_impressiontracker: PropTypes.string,
      program_branding_click_tracker: PropTypes.string,
      program_branding_impression_tracker: PropTypes.string,
      social_title: PropTypes.string.isRequired
    }),
    campaign_branding_image_mobile_url: PropTypes.string,
    primary_category: PropTypes.object.isRequired,
    program_branding_image_url: PropTypes.string,
    url_full: PropTypes.string.isRequired.isRequired
  }).isRequired,
  episode: PropTypes.shape({
    title: PropTypes.string.isRequired
  }).isRequired,
  meta_title: PropTypes.string.isRequired,
  meta_description: PropTypes.string.isRequired,
  show: PropTypes.shape({
    display_name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired
  }).isRequired,
  spotID: PropTypes.string,
  textSections: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired
    }).isRequired
  )
};

EpisodeBody.contextTypes = {
  edition_data: PropTypes.shape({
    config: PropTypes.shape({
      youtube_channel: PropTypes.string
    })
  })
};

export default injectIntl(EpisodeBody);
